import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchOpenAIChatCompletion } from '../lib/openaiClient';
import { supabase, Profile } from '../lib/supabase';
import './ProfileBuilder.css';
import { AcademicInstitutionCombobox } from '../components/AcademicInstitutionCombobox';
import { MajorMinorCombobox } from '../components/MajorMinorCombobox';
import hbsLogo from '../assets/HBS_LOGO.png';
import stanfordLogo from '../assets/STANFORD_LOGO.png';
import whartonLogo from '../assets/WHARTON_LOGO.png';
import columbiaLogo from '../assets/COLUMBIA_LOGO.png';
import mitLogo from '../assets/MIT_LOGO.png';
import chicagoBoothLogo from '../assets/CHICAGO_BOOTH_LOGO.png';
import kelloggLogo from '../assets/KELLOGG_LOGO.png';
import berkeleyHaasLogo from '../assets/BERKELEY_HAAS_LOGO.png';
import yaleSomLogo from '../assets/YALE_SOM_LOGO.png';
import dardenLogo from '../assets/DARDEN_LOGO.png';
import uclaAndersonLogo from '../assets/UCLA_ANDERSON_LOGO.png';
import emoryGoizuetaLogo from '../assets/EMORY_GOIZUETA_LOGO.png';

interface AnalysisResult {
  strengths: string[];
  weaknesses: string[];
  strategy: string;
}

const GPA_SCALE_OPTIONS = ['4.0', '4.3', '5.0', '10.0', '100', 'Other'] as const;

function maxGpaForScale(scale: string | undefined): number {
  if (!scale) return 4;
  if (scale === 'Other') return 100;
  const n = parseFloat(scale);
  return Number.isFinite(n) && n > 0 ? n : 4;
}

const ProfileBuilder: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [isTargetSchoolOverflowOpen, setIsTargetSchoolOverflowOpen] = useState(false);
  const [isGpaScaleMenuOpen, setIsGpaScaleMenuOpen] = useState(false);
  const gpaScaleDropdownRef = useRef<HTMLDivElement>(null);
  const [gmatFocusScore, setGmatFocusScore] = useState(645);
  const [toeflScore, setToeflScore] = useState(105);
  const [includeGmat, setIncludeGmat] = useState(true);
  const [includeGmatFocus, setIncludeGmatFocus] = useState(false);
  const [includeGre, setIncludeGre] = useState(true);
  const [includeToefl, setIncludeToefl] = useState(false);
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  const PROFILE_ANALYSIS_STORAGE_KEY = 'deferredly.profileBuilder.analysis.v1';

  // Target schools options
  const targetSchools = [
    // Top 7 (in your requested order)
    'Harvard Business School',
    'Stanford Graduate School of Business',
    'Wharton School',
    'Columbia Business School',
    'MIT Sloan',
    'Kellogg SOM',
    'Chicago Booth',

    // Shown early in the logo row (deferred / regional picks with in-app logos)
    'Yale SOM',
    'Darden School of Business',
    'UCLA Anderson School of Management',
    'Emory Goizueta Business School',

    // Rest (prior ordering; Yale / Darden / UCLA / Emory removed from tail)
    'Berkeley Haas',
    'Cornell Johnson Graduate School of Management',
    'Foster School of Business',
    'IESE',
    'INSEAD',
    'ISB',
    'Kelley School of Business',
    'London Business School',
    'Marshall School of Business',
    'McCombs School of Business',
    'NUS Singapore',
    'NYU Stern School of Business',
    'Ross School of Business',
    'Rotman School of Management',
    'Oxford Said',
    'Tepper School of Business',
    'Tuck School of Business',
    'UNC Kenan-Flagler Business School'
  ];

  const SCHOOL_SHORT_LABELS: Record<string, string> = {
    'Berkeley Haas': 'Berkeley Haas',
    'Chicago Booth': 'Chicago Booth',
    'Columbia Business School': 'Columbia',
    'Cornell Johnson Graduate School of Management': 'Cornell Johnson',
    'Emory Goizueta Business School': 'Emory Goizueta',
    'Foster School of Business': 'Foster',
    'Harvard Business School': 'Harvard Business School',
    'IESE': 'IESE',
    'INSEAD': 'INSEAD',
    'ISB': 'ISB',
    'Kelley School of Business': 'Kelley',
    'Kellogg SOM': 'Kellogg SOM',
    'London Business School': 'LBS',
    'Marshall School of Business': 'Marshall',
    'McCombs School of Business': 'McCombs',
    'MIT Sloan': 'MIT Sloan',
    'NUS Singapore': 'NUS Singapore',
    'NYU Stern School of Business': 'NYU Stern',
    'Ross School of Business': 'Ross (Michigan)',
    'Rotman School of Management': 'Rotman',
    'Oxford Said': 'Said Oxford',
    'Stanford Graduate School of Business': 'Stanford GSB',
    'Tepper School of Business': 'Tepper',
    'Tuck School of Business': 'Tuck (Dartmouth)',
    'UCLA Anderson School of Management': 'UCLA Anderson',
    'UNC Kenan-Flagler Business School': 'UNC Kenan-Flagler',
    'Darden School of Business': 'UVA Darden',
    'Wharton School': 'Wharton',
    'Yale SOM': 'Yale SOM'
  };

  const getSchoolShortLabel = (fullName: string) => {
    return SCHOOL_SHORT_LABELS[fullName] ?? fullName;
  };

  const SCHOOL_LOGO_BY_NAME: Record<string, string> = {
    'Harvard Business School': hbsLogo,
    'Stanford Graduate School of Business': stanfordLogo,
    'Wharton School': whartonLogo,
    'Columbia Business School': columbiaLogo,
    'MIT Sloan': mitLogo,
    'Chicago Booth': chicagoBoothLogo,
    'Kellogg SOM': kelloggLogo,
    'Berkeley Haas': berkeleyHaasLogo,
    'Emory Goizueta Business School': emoryGoizuetaLogo,
    'UCLA Anderson School of Management': uclaAndersonLogo,
    'Darden School of Business': dardenLogo,
    'Yale SOM': yaleSomLogo
  };

  /** Automatically reduce visible logo chips on smaller screens; overflow moves into the "..." menu. */
  const targetSchoolLogoVisibleCount = (() => {
    if (viewportWidth >= 1500) return 12;
    if (viewportWidth >= 1300) return 10;
    if (viewportWidth >= 1100) return 8;
    if (viewportWidth >= 900) return 7;
    if (viewportWidth >= 700) return 6;
    return 4;
  })();
  const visibleTargetSchoolOptions = targetSchools.slice(0, targetSchoolLogoVisibleCount);
  const overflowTargetSchoolOptions = targetSchools.slice(targetSchoolLogoVisibleCount);

  const loadSavedProfiles = useCallback(async () => {
    if (!user?.id) {
      setProfile({});
      return;
    }
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      if (data) setProfile(data as Profile);
    } catch (error) {
      console.error('Error loading saved profiles:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    void loadSavedProfiles();
  }, [loadSavedProfiles]);

  // Restore the most recent generated analysis after navigation (guests only).
  // Signed-in users load profile from Supabase in loadSavedProfiles.
  useEffect(() => {
    if (user?.id) return;
    try {
      const raw = localStorage.getItem(PROFILE_ANALYSIS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { profile?: Profile; analysis?: AnalysisResult | null };
      if (parsed?.profile) setProfile(parsed.profile);
      if (parsed?.analysis) setAnalysis(parsed.analysis);
    } catch (err) {
      console.warn('Failed to restore cached profile analysis:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isGpaScaleMenuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const root = gpaScaleDropdownRef.current;
      if (root && !root.contains(e.target as Node)) setIsGpaScaleMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsGpaScaleMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isGpaScaleMenuOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleInputChange = (field: keyof Profile, value: any) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field: 'target_schools', value: string[]) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!user?.id) {
      setMessage('Sign in to save your profile to your account.');
      setMessageType('error');
      return;
    }
    try {
      setIsLoading(true);
      setMessage('');

      const payload: Profile & { user_id: string } = {
        ...profile,
        user_id: user.id
      };

      const { error } = await supabase.from('profiles').upsert(payload, { onConflict: 'user_id' });

      if (error) throw error;

      setMessage('Profile saved successfully!');
      setMessageType('success');
      await loadSavedProfiles();
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage('Error saving profile. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  const generateAnalysis = async () => {
    try {
      setIsAnalyzing(true);
      setMessage('');

      const profileText = `
        Academic Institution: ${profile.academic_institution || 'Not specified'}
        Major/Minor: ${profile.major_minor || 'Not specified'}
        GPA: ${profile.gpa || 'Not specified'} (${profile.gpa_scale || 'Not specified'})
        GMAT Score: ${includeGmat ? profile.gmat_score || 'Not specified' : 'Not provided'}
        GMAT Focus Score: ${includeGmatFocus ? gmatFocusScore || 'Not specified' : 'Not provided'}
        GRE Score: ${includeGre ? profile.gre_score || 'Not specified' : 'Not provided'}
        TOEFL Score: ${includeToefl ? toeflScore || 'Not specified' : 'Not provided'}
        Extracurriculars: ${profile.extracurriculars || 'Not specified'}
        Work Experience: ${profile.work_experience || 'Not specified'}
        Target Schools: ${profile.target_schools?.join(', ') || 'Not specified'}
        Desired Outcomes: ${profile.desired_outcomes || 'Not specified'}
        Why MBA: ${profile.why_mba || 'Not specified'}
      `;

      const analysisText = await fetchOpenAIChatCompletion({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: `You are an expert MBA admissions consultant analyzing a candidate's profile. Based on the provided information, provide a comprehensive analysis in the following EXACT format:

STRENGTHS:
- High GPA (if GPA is 3.5+)
- STEM Major (if major is technical)
- High Test Scores (if GMAT 700+ or GRE 320+)
- Strong Work Experience (if relevant experience)
- Leadership Experience (if extracurriculars show leadership)
- Target School Fit (if schools match profile)

WEAKNESSES:
- Low GPA (if GPA is below 3.3)
- Non-traditional Background (if lacking business experience)
- Low Test Scores (if GMAT below 650 or GRE below 310)
- Limited Work Experience (if minimal professional experience)
- Lack of Leadership (if no leadership roles)
- Unclear Career Goals (if desired outcomes / why MBA are unclear)

STRATEGY:
Provide detailed advice in paragraph form with **bold headings** for each key point. Use **bold text** instead of asterisks. Break into multiple paragraphs for readability. Focus on how to leverage strengths and address weaknesses in MBA application essays. Include specific examples and actionable steps.

Be honest but encouraging. Provide specific, actionable advice that will help the candidate improve their application.`
          },
          {
            role: 'user',
            content: `Please analyze this MBA candidate profile:\n\n${profileText}`
          }
        ],
        max_tokens: 1000,
        temperature: 0.7
      });

      // Parse the response into structured format
      const strengthsMatch = analysisText.match(/STRENGTHS:([\s\S]*?)(?:WEAKNESSES:|$)/i);
      const weaknessesMatch = analysisText.match(/WEAKNESSES:([\s\S]*?)(?:STRATEGY:|$)/i);
      const strategyMatch = analysisText.match(/STRATEGY:([\s\S]*)/i);

      // Extract strengths - look for bullet points or numbered lists
      const strengths = strengthsMatch?.[1] 
        ? strengthsMatch[1]
            .split('\n')
            .map((line: string) => line.trim())
            .filter((line: string) => line && (line.startsWith('-') || line.startsWith('•') || line.match(/^\d+\./)))
            .map((line: string) => line.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '').trim())
            .filter((line: string) => line.length > 0)
            .map((line: string) => line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')) // Convert **text** to <strong>text</strong>
        : [];

      // Extract weaknesses - look for bullet points or numbered lists
      const weaknesses = weaknessesMatch?.[1]
        ? weaknessesMatch[1]
            .split('\n')
            .map((line: string) => line.trim())
            .filter((line: string) => line && (line.startsWith('-') || line.startsWith('•') || line.match(/^\d+\./)))
            .map((line: string) => line.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '').trim())
            .filter((line: string) => line.length > 0)
            .map((line: string) => line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')) // Convert **text** to <strong>text</strong>
        : [];

      // Extract strategy and clean up formatting
      let strategy = strategyMatch?.[1]?.trim() || '';
      
      // Clean up asterisks and format properly
      strategy = strategy
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Convert **text** to <strong>text</strong>
        .replace(/\*(.*?)\*/g, '<strong>$1</strong>') // Convert *text* to <strong>text</strong>
        .replace(/\n\n/g, '</p><p>') // Convert double newlines to paragraph breaks
        .replace(/\n/g, ' ') // Convert single newlines to spaces
        .replace(/^\s*/, '<p>') // Add opening paragraph tag
        .replace(/\s*$/, '</p>'); // Add closing paragraph tag

      // If no paragraphs were created, wrap the whole thing
      if (!strategy.includes('<p>')) {
        strategy = `<p>${strategy}</p>`;
      }

      const nextAnalysis = {
        strengths,
        weaknesses,
        strategy
      };

      setAnalysis(nextAnalysis);
      try {
        localStorage.setItem(
          PROFILE_ANALYSIS_STORAGE_KEY,
          JSON.stringify({ profile, analysis: nextAnalysis })
        );
      } catch {
        // ignore (e.g. storage full / privacy mode)
      }

    } catch (error) {
      console.error('Error generating analysis:', error);
      setMessage('Error generating analysis. Please try again.');
      setMessageType('error');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="profile-builder">
      <div className="profile-container">
        <div className="profile-header">
          <h1 className="profile-title">Profile Builder</h1>
          <p className="profile-subtitle">Build your comprehensive MBA application profile</p>
        </div>

        {!user && (
          <div className="profile-auth-callout" role="status">
            <p>
              <strong>Sign in</strong> to save your MBA profile to your account and sync it across devices.{' '}
              <Link to="/login">Log in</Link> or <Link to="/signup">create an account</Link>.
            </p>
          </div>
        )}

        <div className="profile-form">
          {/* Academic Information */}
          <div className="form-section">
            <h2 className="section-title">Academic Information</h2>
            <div className="form-grid form-grid--academic-info">
              <div className="form-group">
                <label htmlFor="academic_institution">Academic Institution</label>
                <AcademicInstitutionCombobox
                  id="academic_institution"
                  value={profile.academic_institution || ''}
                  onChange={(next) => handleInputChange('academic_institution', next)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="major_minor">Major/Minor</label>
                <MajorMinorCombobox
                  id="major_minor"
                  value={profile.major_minor || ''}
                  onChange={(next) => handleInputChange('major_minor', next)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="gpa">Undergraduate GPA</label>
                <input
                  type="number"
                  id="gpa"
                  step="0.01"
                  min="0"
                  max={maxGpaForScale(profile.gpa_scale)}
                  value={profile.gpa || ''}
                  onChange={(e) => handleInputChange('gpa', parseFloat(e.target.value))}
                  placeholder="3.75"
                />
              </div>
              <div className="form-group">
                <label htmlFor="gpa_scale">GPA Scale</label>
                <div className="profile-gpa-scale-dropdown" ref={gpaScaleDropdownRef}>
                  <button
                    type="button"
                    id="gpa_scale"
                    className={`profile-gpa-scale-trigger ${isGpaScaleMenuOpen ? 'open' : ''} ${
                      !profile.gpa_scale ? 'placeholder' : ''
                    }`}
                    aria-haspopup="listbox"
                    aria-expanded={isGpaScaleMenuOpen}
                    onClick={() => setIsGpaScaleMenuOpen((o) => !o)}
                  >
                    <span className="profile-gpa-scale-trigger-text">
                      {profile.gpa_scale || 'Select scale'}
                    </span>
                    <span className="profile-gpa-scale-trigger-chevron" aria-hidden />
                  </button>
                  {isGpaScaleMenuOpen && (
                    <ul className="profile-gpa-scale-menu" role="listbox" aria-label="GPA scale">
                      <li>
                        <button
                          type="button"
                          role="option"
                          aria-selected={!profile.gpa_scale}
                          className={`profile-gpa-scale-option ${!profile.gpa_scale ? 'active' : ''}`}
                          onClick={() => {
                            handleInputChange('gpa_scale', '');
                            setIsGpaScaleMenuOpen(false);
                          }}
                        >
                          Select scale
                        </button>
                      </li>
                      {GPA_SCALE_OPTIONS.map((opt) => (
                        <li key={opt}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={profile.gpa_scale === opt}
                            className={`profile-gpa-scale-option ${profile.gpa_scale === opt ? 'active' : ''}`}
                            onClick={() => {
                              handleInputChange('gpa_scale', opt);
                              setIsGpaScaleMenuOpen(false);
                            }}
                          >
                            {opt}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Test Scores */}
          <div className="form-section">
            <h2 className="section-title">Test Scores</h2>
            <div className="test-score-sliders">
              <div className={`test-score-slider-card ${includeGmat ? '' : 'muted'}`}>
                <div className="test-score-slider-header">
                  <label htmlFor="gmat_score">GMAT</label>
                  <div className="test-score-slider-header-right">
                    <span>{profile.gmat_score ?? 650}</span>
                    <label className="test-score-toggle">
                      <input
                        type="checkbox"
                        checked={includeGmat}
                        onChange={(e) => setIncludeGmat(e.target.checked)}
                      />
                      <span>Include</span>
                    </label>
                  </div>
                </div>
                <input
                  type="range"
                  id="gmat_score"
                  min="200"
                  max="800"
                  step="10"
                  value={profile.gmat_score ?? 650}
                  onChange={(e) => handleInputChange('gmat_score', parseInt(e.target.value, 10))}
                  className="test-score-slider"
                  disabled={!includeGmat}
                />
                <div className="test-score-slider-scale">
                  <span>200</span>
                  <span>800</span>
                </div>
              </div>

              <div className={`test-score-slider-card ${includeGmatFocus ? '' : 'muted'}`}>
                <div className="test-score-slider-header">
                  <label htmlFor="gmat_focus_score">GMAT Focus</label>
                  <div className="test-score-slider-header-right">
                    <span>{gmatFocusScore}</span>
                    <label className="test-score-toggle">
                      <input
                        type="checkbox"
                        checked={includeGmatFocus}
                        onChange={(e) => setIncludeGmatFocus(e.target.checked)}
                      />
                      <span>Include</span>
                    </label>
                  </div>
                </div>
                <input
                  type="range"
                  id="gmat_focus_score"
                  min="205"
                  max="805"
                  step="10"
                  value={gmatFocusScore}
                  onChange={(e) => setGmatFocusScore(parseInt(e.target.value, 10))}
                  className="test-score-slider"
                  disabled={!includeGmatFocus}
                />
                <div className="test-score-slider-scale">
                  <span>205</span>
                  <span>805</span>
                </div>
              </div>

              <div className={`test-score-slider-card ${includeGre ? '' : 'muted'}`}>
                <div className="test-score-slider-header">
                  <label htmlFor="gre_score">GRE</label>
                  <div className="test-score-slider-header-right">
                    <span>{profile.gre_score ?? 320}</span>
                    <label className="test-score-toggle">
                      <input
                        type="checkbox"
                        checked={includeGre}
                        onChange={(e) => setIncludeGre(e.target.checked)}
                      />
                      <span>Include</span>
                    </label>
                  </div>
                </div>
                <input
                  type="range"
                  id="gre_score"
                  min="260"
                  max="340"
                  step="1"
                  value={profile.gre_score ?? 320}
                  onChange={(e) => handleInputChange('gre_score', parseInt(e.target.value, 10))}
                  className="test-score-slider"
                  disabled={!includeGre}
                />
                <div className="test-score-slider-scale">
                  <span>260</span>
                  <span>340</span>
                </div>
              </div>

              <div className={`test-score-slider-card ${includeToefl ? '' : 'muted'}`}>
                <div className="test-score-slider-header">
                  <label htmlFor="toefl_score">TOEFL</label>
                  <div className="test-score-slider-header-right">
                    <span>{toeflScore}</span>
                    <label className="test-score-toggle">
                      <input
                        type="checkbox"
                        checked={includeToefl}
                        onChange={(e) => setIncludeToefl(e.target.checked)}
                      />
                      <span>Include</span>
                    </label>
                  </div>
                </div>
                <input
                  type="range"
                  id="toefl_score"
                  min="0"
                  max="120"
                  step="1"
                  value={toeflScore}
                  onChange={(e) => setToeflScore(parseInt(e.target.value, 10))}
                  className="test-score-slider"
                  disabled={!includeToefl}
                />
                <div className="test-score-slider-scale">
                  <span>0</span>
                  <span>120</span>
                </div>
              </div>
            </div>
          </div>

          {/* Targets */}
          <div className="form-section">
            <h2 className="section-title">Target Schools</h2>
            <div className="form-group">
              <label htmlFor="target_schools">Target Schools/Programs</label>
              <div className="target-schools-picker">
                <div className="target-schools-logo-picker" role="group" aria-label="Select target schools">
                  {visibleTargetSchoolOptions.map((school) => {
                    const isSelected = (profile.target_schools || []).includes(school);
                    const logoSrc = SCHOOL_LOGO_BY_NAME[school];
                    return (
                      <button
                        key={school}
                        type="button"
                        className={`target-school-logo-button ${isSelected ? 'selected' : ''}`}
                        title={school}
                        onClick={() => {
                          const current = profile.target_schools || [];
                          if (isSelected) {
                            handleArrayChange('target_schools', current.filter((s) => s !== school));
                            return;
                          }
                          if (current.length >= 10) return;
                          handleArrayChange('target_schools', [...current, school]);
                        }}
                        disabled={!isSelected && (profile.target_schools || []).length >= 10}
                      >
                        {logoSrc ? (
                          <img src={logoSrc} alt="" className="target-school-logo-image" />
                        ) : (
                          <span className="target-school-logo-fallback">{getSchoolShortLabel(school)}</span>
                        )}
                      </button>
                    );
                  })}

                  <div className="target-schools-overflow">
                    <button
                      type="button"
                      className={`target-school-logo-button target-school-overflow-trigger ${isTargetSchoolOverflowOpen ? 'selected' : ''}`}
                      onClick={() => setIsTargetSchoolOverflowOpen((v) => !v)}
                      aria-label="More schools"
                    >
                      ...
                    </button>
                    {isTargetSchoolOverflowOpen && (
                      <div className="target-schools-overflow-menu">
                        {overflowTargetSchoolOptions.map((school) => {
                          const isSelected = (profile.target_schools || []).includes(school);
                          const logoSrc = SCHOOL_LOGO_BY_NAME[school];
                          return (
                            <button
                              key={school}
                              type="button"
                              className={`target-schools-overflow-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                const current = profile.target_schools || [];
                                if (isSelected) {
                                  handleArrayChange('target_schools', current.filter((s) => s !== school));
                                } else if (current.length < 10) {
                                  handleArrayChange('target_schools', [...current, school]);
                                }
                                setIsTargetSchoolOverflowOpen(false);
                              }}
                            >
                              {logoSrc ? (
                                <img src={logoSrc} alt="" className="target-schools-overflow-logo" />
                              ) : (
                                <span className="target-schools-overflow-fallback" />
                              )}
                              <span>{getSchoolShortLabel(school)}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="target-schools-selected" aria-label="Selected target schools">
                  {(profile.target_schools || []).length === 0 ? (
                    <div className="target-schools-empty">No programs selected yet.</div>
                  ) : (
                    (profile.target_schools || []).map((school) => (
                      <span key={school} className="target-school-pill">
                        {getSchoolShortLabel(school)}
                        <button
                          type="button"
                          aria-label={`Remove ${school}`}
                          onClick={() => {
                            const current = profile.target_schools || [];
                            handleArrayChange(
                              'target_schools',
                              current.filter((s) => s !== school)
                            );
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Experience */}
          <div className="form-section">
            <h2 className="section-title">Experience & Activities</h2>
            <div className="form-group">
              <label htmlFor="extracurriculars">Extracurriculars (with leadership positions)</label>
              <textarea
                id="extracurriculars"
                value={profile.extracurriculars || ''}
                onChange={(e) => handleInputChange('extracurriculars', e.target.value)}
                placeholder="e.g., President of Student Government, Captain of Debate Team, Volunteer at Local Food Bank"
                rows={4}
              />
            </div>
            <div className="form-group">
              <label htmlFor="work_experience">Work Experience (company and role)</label>
              <textarea
                id="work_experience"
                value={profile.work_experience || ''}
                onChange={(e) => handleInputChange('work_experience', e.target.value)}
                placeholder="e.g., McKinsey & Company - Business Analyst, Google - Product Manager Intern"
                rows={4}
              />
            </div>
          </div>

          {/* Career Goals */}
          <div className="form-section">
            <h2 className="section-title">Career Goals</h2>

            <div className="form-group">
              <label htmlFor="desired_outcomes">Desired Outcomes</label>
              <textarea
                id="desired_outcomes"
                value={profile.desired_outcomes || ''}
                onChange={(e) => handleInputChange('desired_outcomes', e.target.value)}
                placeholder="What are your desired short-term and long-term outcomes? (e.g., roles, impact, industry direction)"
                rows={4}
              />
            </div>

            <div className="form-group">
              <label htmlFor="why_mba">Why MBA</label>
              <textarea
                id="why_mba"
                value={profile.why_mba || ''}
                onChange={(e) => handleInputChange('why_mba', e.target.value)}
                placeholder="Why do you need an MBA to achieve these goals?"
                rows={4}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="save-button"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Profile'}
            </button>
            <button
              type="button"
              className="analyze-button"
              onClick={generateAnalysis}
              disabled={isAnalyzing}
            >
              {isAnalyzing ? 'Analyzing...' : 'Generate Profile Analysis'}
            </button>
          </div>

          {message && (
            <div className={`profile-message ${messageType}`}>
              {message}
            </div>
          )}
        </div>

        {/* Analysis Results */}
        <div className="analysis-results">
          <h2 className="analysis-title">Profile Analysis</h2>
          <div className="analysis-grid">
            <div className="analysis-box strengths">
              <h3>Strengths</h3>
              {analysis?.strengths?.length ? (
                <ul>
                  {analysis.strengths.map((strength, index) => (
                    <li key={index} dangerouslySetInnerHTML={{ __html: strength }} />
                  ))}
                </ul>
              ) : (
                <p className="analysis-empty-message">
                  Your analysis will highlight the strongest parts of your profile and how to emphasize them in your applications.
                </p>
              )}
            </div>
            <div className="analysis-box weaknesses">
              <h3>Weaknesses</h3>
              {analysis?.weaknesses?.length ? (
                <ul>
                  {analysis.weaknesses.map((weakness, index) => (
                    <li key={index} dangerouslySetInnerHTML={{ __html: weakness }} />
                  ))}
                </ul>
              ) : (
                <p className="analysis-empty-message">
                  Your analysis will identify your main improvement areas and where admissions readers may have concerns.
                </p>
              )}
            </div>
          </div>
          <div className="analysis-box strategy">
            <h3>Application Strategy</h3>
            {analysis?.strategy ? (
              <div dangerouslySetInnerHTML={{ __html: analysis.strategy }} />
            ) : (
              <div>
                <p className="analysis-empty-message">
                  Your application strategy will explain how to leverage your strengths and address weaknesses in essays, recommenders, and interviews.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileBuilder;
